/**
 * Auth mutations + hydration. Login/logout are mutations. `useHydrateAuth`
 * runs once at boot to pick up an existing session from the refresh cookie.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { LoginRequest, LoginResponse, MeResponse, RefreshResponse } from '@fuelsync/common';
import { api } from '@/services/api';
import { useAuthStore } from './store';

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: LoginRequest) => api.post<LoginResponse>('/auth/login', input),
    onSuccess: (res) => {
      setSession({
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
        user: res.user,
      });
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: async () => {
      // In an impersonation tab, calling /auth/logout would send the
      // ADMIN's refresh cookie (same origin, browser attaches automatically)
      // and revoke it — killing the admin's own session in their other tab.
      // The impersonation JWT has no server-side session to revoke, so we
      // just wipe local state here and the token dies on its own within
      // 30 minutes.
      if (useAuthStore.getState().isImpersonating) return;
      await api.post<void>('/auth/logout');
    },
    onSettled: () => clear(),
  });
}

/**
 * Called once at app boot inside <App>. Attempts a silent refresh; on success
 * fetches /auth/me to populate the user object. On failure the store stays
 * empty and the router shows /login.
 *
 * Impersonation branch: when the tab was opened via
 *   /?impersonation_token=<jwt>&impersonation_expires_at=<iso>
 * we skip the refresh dance (impersonation tokens have no refresh cookie and
 * would just clobber our impersonation session with the admin's own) and
 * seed the session directly from the URL. The URL is then scrubbed so a
 * page reload doesn't re-consume the same token.
 */
export function useHydrateAuth() {
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);

  return useQuery({
    queryKey: ['auth', 'hydrate'],
    queryFn: async () => {
      const params = new URLSearchParams(window.location.search);
      const impToken = params.get('impersonation_token');
      const impExpiry = params.get('impersonation_expires_at');
      if (impToken && impExpiry) {
        // Mark the impersonation flag BEFORE any network call, so that a 401
        // during /auth/me (or anything else) can't be silently upgraded to
        // an admin session via the browser's stored refresh cookie.
        useAuthStore.getState().markImpersonating();
        try {
          setAccessToken(impToken, impExpiry);
          const me = await api.get<MeResponse>('/auth/me');
          setUser(me);
          // Strip the token from the URL so it doesn't linger in bookmarks
          // or history entries and can't be replayed on refresh.
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
          return me;
        } catch {
          clear();
          return null;
        }
      }
      try {
        const refreshed = await api.post<RefreshResponse>('/auth/refresh', undefined, {
          skipAuthRetry: true,
        });
        setAccessToken(refreshed.accessToken, refreshed.accessTokenExpiresAt);
        const me = await api.get<MeResponse>('/auth/me');
        setUser(me);
        return me;
      } catch {
        clear();
        return null;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}
